import React, { useCallback, useRef, useState } from 'react'
import AMSListing from '../../components/listing/AMSListing';
import { useGetAllFieldsQuery } from '../../api/formsApiSlice';
import { ActionType, Column, Field, FieldGroupEnum, ImportTypeEnum } from '../../types/types';
import { useLocation, useNavigate } from 'react-router-dom';
import ImportModal from './Modals/ImportModal';
import { useGetAllAccountsQuery, useGetImportContactQuery, useImportStartMutation } from '../../api/contactApiSlice';
import FileHeader from './FileHeader';
import StyledText from '../../components/StyledComponents/StyledText';
import { CorporateTheme, font } from '../../utils/themes';
import ArrowIcon from '../../assets/icons/ArrowIcon';
import styled from 'styled-components';
import { useTheme } from '../../middleware/ThemeContext';
import { formatFileSize } from '../../utils/helper';
import moment from 'moment';
import { Button } from '../../components/form/Button';
import CrossIcon from '../../assets/icons/CrossIcon';
import ImportIcon from '../../assets/icons/ImportIcon';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { useToast } from '../../components/toast/ToastContext';

const Preview = () => {
    const navigate = useNavigate();
    const { theme } = useTheme();
    const location = useLocation();
    const showToast = useToast();
    let PreviewResponse = location.state;

    const [importContacts, { isLoading: importLoading }] = useImportStartMutation();
    const result: Column[] = Object.entries(PreviewResponse?.data?.mapping || {}).map(
        ([key, value]) => ({
            id: String(value),
            label: String(value)
        })
    );

    const lastParamsRef = useRef<{
        page: number;
        perPage: number;
        sort?: { field: string; direction: "ASC" | "DESC" };
        query?: string;
        filters: Record<string, any>;
    }>({
        page: 1,
        perPage: 10,
        filters: {},
    });

    const [initialFilters, setInitialFilters] = useState({
        ...lastParamsRef.current,
        query: "",
    });

    const { sort, filters, ...rest } = initialFilters;
    const {
        data: UserDetails
    } = useGetAllAccountsQuery(
        {
            refetchOnMountOrArgChange: true,
        }
    );

    const staticFormsColumns: Column[] = result



    const filterGroups = [
        {
            key: 'importType',
            label: 'Import Type',
            dataType: 'multi-select',
            options: Object.values(ImportTypeEnum).map((value) => ({
                value,
                label: value
                    .toLowerCase()
                    .split('_')
                    .map((word) => word[0].toUpperCase() + word.slice(1))
                    .join(' '),
            })),
        },
        {
            key: 'userName',
            label: 'Imported By',
            dataType: 'multi-select',
            options: UserDetails?.options,
        },
    ];


    const params = {
        ...rest,
        ...filters,
        sort: sort?.field,
        sortDir: sort?.direction,
    };
    const {
        data: AllContacts,
        refetch,
        isLoading,
        isFetching,
    } = useGetImportContactQuery(
        { ...params },
        {
            refetchOnMountOrArgChange: true,
        }
    );
    let summary = PreviewResponse?.data?.importJobDetail?.importJob;

    const fileData = {
        totalContact: `${PreviewResponse?.data?.meta?.totalRecords} found`,
        importName: summary?.fileName,
        importType: summary?.scope?.displayName,
        fileSize: summary?.meta?.fileSize ? formatFileSize(summary?.meta?.fileSize) : "-",
        date: moment(summary?.createdAt).format("MM/DD/YYYY"),
        importBy: `${summary.account?.firstName}` + ' ' + `${summary.account?.lastName}`,
    };


    const labels = {
        totalContact: "Total Contacts ",
        importName: "Import Name",
        importType: "Import Type",
        fileSize: "File Size",
        date: "Import Date",
        importBy: "Import By",
    };

    const handleImport = () => {
        importContacts(summary?.id).then((response) => {
            if (response?.error) (
                showToast("Failed", "Failed to start Import", "error")
            )
            else {
                showToast("Import in Progress", "Your contacts are being imported. This may take a few moments hang tight!", "success")
                navigate("/home/imports");
            }

        }).catch((err) => {
            console.log(err, 'err handleImport');
        })
    };

    return (
        <>
            <div className='d-flex gap-2 mb-4 px-4 mt-4'>
                <div onClick={() => navigate(`/home/mapping/${PreviewResponse?.data?.importJobDetail?.importJob?.id}/${PreviewResponse?.data?.importJobDetail?.importJob?.scopeId}`)} className='cursor-pointer'>
                    <ArrowIcon color={theme?.CorporateTheme?.colors?.headerColor} />
                </div>
                <div>
                    <StyledText fontSize='20px' fontWeight={700} fontFamily={font.raleway}>
                        Summary of your Contact Import
                    </StyledText>
                    <StyledText textColor={theme?.CorporateTheme?.colors?.textSecondary} className='mt-1' fontSize='14px' fontWeight={400} fontFamily={font.raleway}>
                        Review the details below before importing your contacts into the system
                    </StyledText>
                </div>
            </div >

            <div className='px-4'>
                <FileHeader data={fileData} labels={labels} />
            </div>
            <AMSListing

                routeKey="preview"
                noDataTitle="No Import Found"
                noDataSubtitle="Your import list is currently empty. Start adding new imports to keep everything organized and easy to find."
                filterGroups={filterGroups}
                title="Field Mapping Overview."
                subtitle=""
                columns={staticFormsColumns}
                // data={AllFields?.fields?.map((field: Field) => ({
                //   ...field,
                //   fieldScopes: field.isGlobal ? 'Global' : field.fieldScopes?.map(scope => scope.scope?.displayName).filter(Boolean).join(', ')
                // })) || []}
                showPagination={false}
                data={PreviewResponse?.data?.preview || []}
                totalItems={AllContacts?.totalCount}
                loading={isLoading || isFetching}
                skeletonHeight={'42px'}
                noTableAction
                onAction={useCallback(
                    async (action: ActionType, id: string) => {
                        const numericId = Number(id);
                        if (action === "view") {
                            navigate(`/home/records/${numericId}`);
                            return;
                        }

                    },
                    [refetch, AllContacts?.data]
                )}
                setInitialFilters={setInitialFilters}
                containerHeight={490}
                hideActionColumn
            />

            <StyledDiv>
                <StyledText fontFamily={font.raleway} fontSize="14px" textColor={theme?.CorporateTheme?.colors?.headerColor}><strong>Note:</strong> Duplicates will be skipped and will be able to download after import.</StyledText>
            </StyledDiv>

            <div className='d-flex justify-content-end gap-2 pe-4 mb-5'>
                <Button
                    theme={theme}
                    btnBg={theme?.CorporateTheme?.colors?.filterBG}
                    type="button"
                    $variant="customButton"
                    className="px-3"
                    onClick={() => navigate('/home/imports')}
                >
                    <CrossIcon />
                    Cancel
                </Button>
                <Button
                    theme={theme}
                    btnBg={theme?.CreateFormTheme?.colors?.primary}
                    color={theme?.CreateFormTheme?.colors?.background}
                    type="button"
                    $variant="customButton"
                    className="px-3"
                    onClick={handleImport}
                    disabled={importLoading}
                >
                    {importLoading ? <LoadingSpinner color={theme?.CreateFormTheme?.colors?.background} size='18px' /> : <ImportIcon color={theme?.CreateFormTheme?.colors?.background} />}
                    Import
                </Button>
            </div>
        </>
    )
}

const StyledDiv = styled.div`
border-radius: 5px;
border: 1px solid #D9D9D9;
border-style: dashed;
width: max-content;
margin-left: 30px;
padding:16px;
`

export default Preview;
